
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { MedewerkerResponse, MedewerkerInsert, MedewerkerUpdate } from "@/integrations/supabase/client";
import { medewerkerSchema, MedewerkerFormValues } from "./types";

export const useMedewerkerForm = (
  medewerker?: MedewerkerResponse,
  onSubmit?: (values: MedewerkerInsert | MedewerkerUpdate) => void
) => {
  // Bepaal de initiële rol op basis van de functie
  const getInitialRole = (functie: string | null): "medewerker" | "planner" | "management" => {
    if (!functie) return "medewerker";
    const functieLower = functie.toLowerCase();
    if (functieLower.includes('management') || functieLower.includes('admin')) {
      return "management";
    } else if (functieLower.includes('planner')) {
      return "planner";
    }
    return "medewerker";
  };

  const form = useForm<MedewerkerFormValues>({
    resolver: zodResolver(medewerkerSchema),
    defaultValues: {
      naam: medewerker?.naam || "",
      roepnaam: (medewerker as any)?.roepnaam || "",
      functie: medewerker?.functie || "",
      afdeling: medewerker?.afdeling || "",
      email: medewerker?.email || "",
      username: medewerker?.username || "",
      password: "",  // leeg bij bewerken
      role: medewerker ? getInitialRole(medewerker.functie) : "medewerker",
    },
  });

  const handleSubmit = (values: MedewerkerFormValues) => {
    if (!onSubmit) return;
    
    // Stel de functie in op basis van de rol
    let functie = values.functie || "";
    if (values.role === "management" && !functie.toLowerCase().includes("management")) {
      functie = functie ? `${functie} (Management)` : "Management";
    } else if (values.role === "planner" && !functie.toLowerCase().includes("planner")) {
      functie = functie ? `${functie} (Planner)` : "Planner";
    }
    
    // Omzetten naar het juiste formaat voor insert/update
    const medewerkerData: any = {
      naam: values.naam,
      roepnaam: values.roepnaam || null,
      functie: functie || null,
      afdeling: values.afdeling || null,
      email: values.email || null,
      username: values.username,
    };

    // Voeg wachtwoord alleen toe als het is ingevuld
    if (values.password && values.password.trim() !== "") {
      medewerkerData.password = values.password;
    }

    // Als we een bestaande medewerker bewerken, voeg dan updated_at toe
    if (medewerker) {
      (medewerkerData as MedewerkerUpdate).updated_at = new Date().toISOString();
    }

    onSubmit(medewerkerData);
  };

  return {
    form,
    handleSubmit: form.handleSubmit(handleSubmit),
    isEditMode: !!medewerker
  };
};
